/**
 * ISSUE-1343. The Archived pools list, v1's see_all_pool.dart with
 * PoolListType.archive: the pools this fan tucked away from their own list.
 * Each card keeps its More options, where the row now reads Unarchive; an
 * open pool still opens from here.
 *
 * server-resolves-client-renders.mdc: `/pool/list-mine` with
 * `view: "archived"` decides which pools are archived; this pane filters
 * nothing and only sorts the way the main list does.
 */
import { usePoolListMine } from "@sportsmet/pool-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useMemo } from "react";

import { PoolCard } from "./PoolCard";
import {
  leagueAbbreviationsByKey,
  resolvePoolsListState,
} from "./poolsListState";

export const ARCHIVED_POOLS_EMPTY_TITLE = "Nothing archived";
export const ARCHIVED_POOLS_EMPTY_BODY =
  "Pools you archive land here. Open a pool's More options on your list and pick Archive.";
export const ARCHIVED_POOLS_FAILED = "We could not load your archived pools.";

export const ArchivedPoolsPane = (props: {
  onSelectPool: (poolId: string) => void;
}): ReactNode => {
  const pools = usePoolListMine({ view: "archived" });
  const catalog = useLeagueCatalogGet({});
  const leagueAbbreviations = useMemo(
    () => leagueAbbreviationsByKey(catalog.data?.data?.sports ?? []),
    [catalog.data],
  );
  const state = resolvePoolsListState({
    isLoading: pools.isLoading,
    hasFailed: pools.isError || Boolean(pools.data?.error),
    items: pools.data?.data?.items ?? null,
    query: "",
    leagueAbbreviations,
  });

  if (state.phase === "loading") {
    return (
      <div className="flex flex-col gap-3" data-testid="archived-pools-loading">
        <Skeleton shape="card" label="Loading your archived pools" />
        <Skeleton shape="card" />
      </div>
    );
  }
  if (state.phase === "failed") {
    return (
      <div data-testid="archived-pools-failed">
        <InlineBanner
          variant="warning"
          message={ARCHIVED_POOLS_FAILED}
          action={{
            label: "Try again",
            onAction: () => {
              void pools.refetch();
            },
          }}
        />
      </div>
    );
  }
  if (state.phase !== "ready") {
    return (
      <div
        className="flex flex-col items-center gap-2 py-10 text-center"
        data-testid="archived-pools-empty"
      >
        <p className="text-body-strong text-ink">
          {ARCHIVED_POOLS_EMPTY_TITLE}
        </p>
        <p className="text-body text-ink-secondary">
          {ARCHIVED_POOLS_EMPTY_BODY}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3" data-testid="archived-pools-stack">
      {state.cards.map((card) => (
        <PoolCard
          key={card.poolId}
          model={card}
          isSelected={false}
          onSelect={props.onSelectPool}
        />
      ))}
    </div>
  );
};
